import { useContext } from 'react';
import { MapContext } from '@agissc/ktag-map';
import useAppState from '../../../AppStore';
import usePrintLayout from './usePrintLayout';

const usePrintArea = () => {
  const { map } = useContext(MapContext);
  const printScale = useAppState((state) => state.printScale);
  const layoutId = useAppState((state) => state.layoutId);
  const orientation = useAppState((state) => state.orientation);
  const formatValue = useAppState((state) => state.formatValue);
  const angle = useAppState((state) => state.angle);
  const printLayers = usePrintLayout();
  const layouts = printLayers.data ?? [];
  const format = layouts
    .find((item) => item.id === layoutId)
    ?.formats.find((item) => item.id === formatValue);

  const { mapHeightInMillimeter, mapWidthInMillimeter } = format?.[orientation] ?? {
    mapHeightInMillimeter: 0,
    mapWidthInMillimeter: 0,
  };

  const getMapSizeInPx = () => {
    const mapSize = map ? map.getSize() || [1, 1] : [1, 1];
    return { mapWidthInPx: mapSize[0], mapHeightInPx: mapSize[1] };
  };

  const getBoundingBoxOfRotatedExtent = () => {
    const { mapWidthInPx, mapHeightInPx } = getMapSizeInPx();
    const angleInRadians = (angle * Math.PI) / 180;
    const widthBoundingBox =
      Math.abs(mapWidthInPx * Math.cos(angleInRadians)) +
      Math.abs(mapHeightInPx * Math.sin(angleInRadians));
    const heightBoundingBox =
      Math.abs(mapWidthInPx * Math.sin(angleInRadians)) +
      Math.abs(mapHeightInPx * Math.cos(angleInRadians));
    return { widthBoundingBox, heightBoundingBox };
  };

  const getPrintSize = () => {
    const printWidth = (mapWidthInMillimeter * (printScale ?? 1)) / 1000;
    const printHeight = (mapHeightInMillimeter * (printScale ?? 1)) / 1000;

    return { printWidth, printHeight };
  };

  const getPrintExtent = () => {
    if (!map) return null;

    const { printWidth, printHeight } = getPrintSize();
    const [centerX, centerY] = map.getView().getCenter() || [0, 0];

    return {
      xMin: centerX - printWidth / 2,
      yMin: centerY - printHeight / 2,
      xMax: centerX + printWidth / 2,
      yMax: centerY + printHeight / 2,
    };
  };

  const getPreviewExtent = () => {
    if (!map) return null;

    const { mapWidthInPx, mapHeightInPx } = getMapSizeInPx();
    const { printWidth, printHeight } = getPrintSize();

    const view = map.getView();
    const extent = view.calculateExtent();
    const center = view.getCenter()!;
    const centerx = ((center[0] - extent[0]) / (extent[2] - extent[0])) * mapWidthInPx;
    const centery = ((extent[3] - center[1]) / (extent[3] - extent[1])) * mapHeightInPx;

    const { widthBoundingBox } = getBoundingBoxOfRotatedExtent();
    const resolution = widthBoundingBox / (extent[2] - extent[0]);

    const printWidthInPx = printWidth * resolution;
    const printHeightInPx = printHeight * resolution;

    return {
      xMin: centerx - printWidthInPx / 2,
      yMin: centery - printHeightInPx / 2,
      xMax: centerx + printWidthInPx / 2,
      yMax: centery + printHeightInPx / 2,
    };
  };

  const setFitExtent = () => {
    if (!map) return;

    const extent = getPrintExtent();
    if (!extent) return;

    const newExtent = [extent.xMin, extent.yMin, extent.xMax, extent.yMax];
    map.getView().fit(newExtent);
  };

  return { getMapSizeInPx, getPreviewExtent, getPrintExtent, setFitExtent };
};

export default usePrintArea;
